import { ComponentType } from 'react';
import type { Agent, AgentCapabilities } from '@/entities/agent';
import { ChatOptionsProps } from './ChatOptions.types';
import { CopyGenerationOptions } from './CopyGeneration';
import { ImageGenerationOptions } from './ImageGeneration';
import { SentinelOptions } from './Sentinel';
import { VideoGenerationOptions } from './VideoGeneration';
import { TranslationAgentOptions } from './TranslationAgent/TranslationAgentOptions';
import { StrategyAgentOptions } from '@/features/chats/ui/ChatOptions/StrategyAgent';
import { TemplatesAgentOptions } from './TemplatesAgent';

/**
 * A registry mapping agent IDs to their corresponding chat option component.
 */
export const Registry: Record<string, ComponentType<ChatOptionsProps>> = {
  'copy-generation': CopyGenerationOptions,
  'image-generation': ImageGenerationOptions,
  'sentinel-buddy-agent': SentinelOptions,
  'strategy-agent-v3': StrategyAgentOptions,
  'video-generation': VideoGenerationOptions,
  'translation-agent': TranslationAgentOptions,
  templates: TemplatesAgentOptions,
};

export interface ChatOptionsChoice {
  Component: ComponentType<ChatOptionsProps & Record<string, unknown>>;
  props: Record<string, unknown>;
}

/**
 * The options the composer offers for an agent: its own when it has them, else the image options
 * for an agent built in Studio with the Generate image tool, set to what its card offers.
 */
export function chatOptionsFor(
  agent: Pick<Agent, 'id'> & { capabilities?: Pick<AgentCapabilities, 'imageGeneration'> },
): ChatOptionsChoice | undefined {
  const own = Registry[agent.id] as ComponentType<ChatOptionsProps> | undefined;
  if (own) return { Component: own, props: {} };
  const image = agent.capabilities?.imageGeneration;
  if (image) {
    return {
      Component: ImageGenerationOptions as ChatOptionsChoice['Component'],
      props: { models: image.models, defaults: image.defaults },
    };
  }
  return undefined;
}
